import { useEffect, useRef, useState, useCallback } from 'react';
import API from '../api/axios';

/* Mark a single record as read on the server. Used by detail views.
   Fire-and-forget; we don't surface failures to the user. */
export function markRecordRead(type, id) {
  if (!type || !id) return Promise.resolve();
  return API.post('/notifications/read', { type, id }).catch(() => {});
}

/* Hook: polls /notifications/poll on a fixed interval, returns the latest
   "new since last poll" items + a total unread count. The first call uses
   the page-load time as `since` so we don't replay history as new arrivals. */
export function useUnread({ intervalMs = 30000, enabled = true } = {}) {
  const [unreadCount, setUnreadCount] = useState(0);
  const [latest, setLatest] = useState([]); // items new since previous poll
  const sinceRef = useRef(null);            // server timestamp from previous response

  const poll = useCallback(async () => {
    try {
      const params = sinceRef.current ? { since: sinceRef.current } : {};
      const r = await API.get('/notifications/poll', { params });
      const d = r.data || {};
      sinceRef.current = d.now || sinceRef.current;
      setUnreadCount(Number(d.unread_count) || 0);
      const news = Array.isArray(d.new) ? d.new : [];
      if (news.length) setLatest(news);
    } catch { /* swallow — next tick will retry */ }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    poll();
    const id = setInterval(poll, intervalMs);
    return () => clearInterval(id);
  }, [enabled, intervalMs, poll]);

  const clearLatest = useCallback(() => setLatest([]), []);
  return { unreadCount, latest, clearLatest, refresh: poll };
}
